import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface FxRates {
  /** Pair rates keyed by "FROM/TO" (e.g. "EUR/USD") from `exchange_rates`. */
  pairs: Record<string, number>;
  /** USD value of one unit of a fiat currency, from the `fiat_currencies` registry. */
  usdPerUnit: Record<string, number>;
  /** Every currency code known to the fiat registry (active or not). */
  fiatCodes: Set<string>;
  /** USD value of one unit of a configured token, from `token_configs.price_usd`. */
  tokenUsdPerUnit: Record<string, number>;
  isLoading: boolean;
  /** True while any rate source is refreshing, including cached background refetches. */
  isFetching: boolean;
  isError: boolean;
}

/**
 * Single source of FX truth for portfolio valuation.
 *
 * Merges `exchange_rates` (pair quotes) with `fiat_currencies.price_usd`
 * (registry USD price per unit) so currencies that only exist in the registry
 * — NGN, KES, GHS, ZMW, ZAR — are still convertible. Consumers must treat a
 * missing rate as "unpriced" and never fall back to 1:1.
 */
export function useFiatRates(): FxRates {
  const pairsQuery = useQuery({
    queryKey: ["fiat-exchange-rates"],
    queryFn: async () => {
      const { data, error } = await supabase.from("exchange_rates").select("currency_pair, rate");
      if (error) throw error;
      const map: Record<string, number> = {};
      (data || []).forEach((r: any) => {
        const rate = Number(r.rate);
        if (r.currency_pair && Number.isFinite(rate) && rate > 0) {
          map[String(r.currency_pair).toUpperCase()] = rate;
        }
      });
      return map;
    },
    staleTime: 5 * 60 * 1000,
  });

  const registryQuery = useQuery({
    queryKey: ["fiat-currencies-usd-rates"],
    queryFn: async () => {
      const { data, error } = await supabase.from("fiat_currencies").select("currency_code, price_usd");
      if (error) throw error;
      const map: Record<string, number> = {};
      (data || []).forEach((r: any) => {
        const p = Number(r.price_usd);
        if (r.currency_code && Number.isFinite(p) && p > 0) {
          map[String(r.currency_code).toUpperCase()] = p;
        }
      });
      return map;
    },
    staleTime: 5 * 60 * 1000,
  });

  const tokenQuery = useQuery({
    queryKey: ["token-configs-usd-rates"],
    queryFn: async () => {
      const { data, error } = await supabase.from("token_configs").select("symbol, price_usd");
      if (error) throw error;
      const map: Record<string, number> = {};
      (data || []).forEach((r: any) => {
        const p = Number(r.price_usd);
        if (r.symbol && Number.isFinite(p) && p > 0) {
          map[String(r.symbol).toUpperCase()] = p;
        }
      });
      return map;
    },
    staleTime: 5 * 60 * 1000,
  });

  const usdPerUnit = registryQuery.data ?? {};

  return {
    pairs: pairsQuery.data ?? {},
    usdPerUnit,
    fiatCodes: new Set(Object.keys(usdPerUnit)),
    tokenUsdPerUnit: tokenQuery.data ?? {},
    isLoading: pairsQuery.isLoading || registryQuery.isLoading || tokenQuery.isLoading,
    isFetching: pairsQuery.isFetching || registryQuery.isFetching || tokenQuery.isFetching,
    isError: pairsQuery.isError || registryQuery.isError || tokenQuery.isError,
  };
}
